import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { usePOSUser } from '../components/contexts/POSUserContext';
import { useStore } from '../components/contexts/StoreContext';

const DEFAULT_TRAINING_DURATION_MINUTES = 90; // Default 90 minutes
const DEFAULT_TRAINING_DURATION_SECONDS = DEFAULT_TRAINING_DURATION_MINUTES * 60; // 5400 seconds

const TrainingModeContext = createContext(undefined);

/**
 * Training Mode Context Provider
 * Manages user-specific training mode state with 90-minute timer
 * Each POS user has independent training mode session
 */
export const TrainingModeContextProvider = ({ children }) => {
  const { currentPOSUser } = usePOSUser();
  const { currentStore } = useStore();
  const [trainingStates, setTrainingStates] = useState({}); // userId -> { isTrainingMode, startTime }
  const [showExpirationModal, setShowExpirationModal] = useState(false);

  // Get training duration from store settings (default: 90 minutes)
  const trainingDurationMinutes = useMemo(() => {
    return currentStore?.trainingModeDurationMinutes || DEFAULT_TRAINING_DURATION_MINUTES;
  }, [currentStore?.trainingModeDurationMinutes]);

  const trainingDurationSeconds = useMemo(() => {
    return trainingDurationMinutes * 60;
  }, [trainingDurationMinutes]);

  // Load training mode state from localStorage on mount
  useEffect(() => {
    const loadTrainingStates = () => {
      const states = {};
      // Load all training mode states from localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('trainingMode_') && !key.slice('trainingMode_'.length).includes('_')) {
          try {
            const userId = key.replace('trainingMode_', '');
            const state = JSON.parse(localStorage.getItem(key));
            // Check if timer has expired
            if (state.startTime) {
              const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
              // Use duration from state if available, otherwise use current store setting
              const duration = state.durationSeconds || trainingDurationSeconds;
              if (elapsed >= duration) {
                // Timer expired - clear state
                localStorage.removeItem(key);
                continue;
              }
            }
            states[userId] = state;
          } catch (error) {
            console.error('Error loading training mode state:', error);
          }
        }
      }
      setTrainingStates(states);
    };

    loadTrainingStates();
  }, [trainingDurationSeconds]);

  // Check and revert expired training modes
  const checkAndRevertIfExpired = useCallback((userId) => {
    if (!userId) return false;

    const storageKey = `trainingMode_${userId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (!stored) {
      // Update state if localStorage was cleared
      setTrainingStates(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      return false;
    }

    try {
      const state = JSON.parse(stored);
      if (state.startTime) {
        const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
        // Use duration from state if available, otherwise use current store setting
        const duration = state.durationSeconds || trainingDurationSeconds;
        if (elapsed >= duration) {
          // Timer expired - clear state
          localStorage.removeItem(storageKey);
          setTrainingStates(prev => {
            const next = { ...prev };
            delete next[userId];
            return next;
          });
          return false;
        }
      }
    } catch (error) {
      console.error('Error checking training mode expiration:', error);
      return false;
    }

    return true;
  }, [trainingDurationSeconds]);

  // Get training mode state for current user
  const getTrainingMode = useCallback((userId) => {
    if (!userId) return false;
    
    // Check if expired (but don't update state here to avoid loops)
    const storageKey = `trainingMode_${userId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (!stored) {
      return false;
    }

    try {
      const state = JSON.parse(stored);
      if (state.startTime) {
        const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
        // Use duration from state if available, otherwise use current store setting
        const duration = state.durationSeconds || trainingDurationSeconds;
        if (elapsed >= duration) {
          // Timer expired - but don't update here, let the interval check handle it
          return false;
        }
      }
    } catch (error) {
      return false;
    }

    const state = trainingStates[userId];
    return state ? state.isTrainingMode : false;
  }, [trainingStates, trainingDurationSeconds]);

  // Get remaining time in seconds
  const getRemainingTime = useCallback((userId) => {
    if (!userId) return 0;

    const storageKey = `trainingMode_${userId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (!stored) return 0;

    try {
      const state = JSON.parse(stored);
      if (state.startTime) {
        const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
        // Use duration from state if available, otherwise use current store setting
        const duration = state.durationSeconds || trainingDurationSeconds;
        const remaining = Math.max(0, duration - elapsed);
        return remaining;
      }
    } catch (error) {
      console.error('Error calculating remaining time:', error);
    }

    return 0;
  }, [trainingDurationSeconds]);

  // Check if Training Mode can be enabled (time restrictions + online check)
  const canEnableTrainingMode = useCallback(async (userId, storeId) => {
    if (!userId || !storeId) {
      return {
        canEnable: false,
        reason: 'User ID and Store ID are required'
      };
    }

    // Check if online - Training Mode requires online connection
    try {
      const connectionMonitor = (await import('../utils/connectionMonitor')).default;
      const isOnline = connectionMonitor.getStatus();
      if (!isOnline) {
        return {
          canEnable: false,
          reason: 'Training Mode is not available offline. Please connect to the internet to enable Training Mode.'
        };
      }
    } catch (error) {
      console.error('[Training Mode] Error checking connection status:', error);
      // If we can't check, assume offline for safety
      return {
        canEnable: false,
        reason: 'Unable to verify internet connection. Training Mode requires an online connection.'
      };
    }

    try {
      // Import required utilities
      const { generateDateCode, getBusinessDate } = await import('../utils/posOrderId');
      const posOrderIdDB = (await import('../utils/indexedDB/posOrderIdDB')).default;
      const storeDB = (await import('../utils/indexedDB/storeDB')).default;
      
      await posOrderIdDB.init();
      await storeDB.init();
      
      // Get station start time
      const stationStartTime = localStorage.getItem(`stationStartTime_${storeId}`) || '04:00';
      
      // Generate date code for today
      const dateCode = await generateDateCode(new Date(), stationStartTime, storeId);
      
      // Check POS order ID counter
      const counterKey = `posOrderId_${storeId}_${dateCode}`;
      const posOrderCounter = await posOrderIdDB.getCounter(counterKey);
      
      console.log('[Training Mode] Time restriction check:', { 
        storeId, 
        dateCode, 
        posOrderCounter,
        stationStartTime 
      });
      
      // Scenario 1: Before first order (counter = 0)
      if (posOrderCounter === 0) {
        return { 
          canEnable: true, 
          reason: 'Before first order of the day' 
        };
      }
      
      // Scenario 2: After end of day (counter > 0, but EOD completed)
      // Get business date in YYYY-MM-DD format
      const businessDate = getBusinessDate(new Date(), stationStartTime);
      const dateStr = businessDate.toISOString().split('T')[0]; // YYYY-MM-DD
      
      console.log('[Training Mode] Checking EOD for date:', dateStr);
      const eodRecord = await storeDB.getEODRecord(storeId, dateStr);
      
      if (eodRecord) {
        // EOD completed - allow Training Mode
        return { 
          canEnable: true, 
          reason: 'After end of day completed' 
        };
      }
      
      // Block: Orders exist but EOD not completed
      return {
        canEnable: false,
        reason: `Cannot enable Training Mode: ${posOrderCounter} order(s) have been created today and End of Day has not been completed. Please complete End of Day first, or wait until tomorrow before the first order.`
      };
      
    } catch (error) {
      console.error('[Training Mode] Error checking time restrictions:', error);
      return {
        canEnable: false,
        reason: 'Error checking Training Mode availability. Please try again.'
      };
    }
  }, []);

  // Toggle training mode for a specific user
  const toggleTrainingMode = useCallback(async (userId, enable, storeId = null) => {
    console.log('[Training Mode] toggleTrainingMode called', { userId, enable, storeId });
    if (!userId) {
      console.error('[Training Mode] Cannot toggle training mode: No user ID provided');
      return;
    }

    const storageKey = `trainingMode_${userId}`;

    if (enable) {
      // Get storeId if not provided
      if (!storeId) {
        storeId = localStorage.getItem('selectedStoreId');
      }
      
      // Validate before enabling
      const validation = await canEnableTrainingMode(userId, storeId);
      if (!validation.canEnable) {
        console.error('[Training Mode] Validation failed:', validation.reason);
        throw new Error(validation.reason);
      }
      
      // Get the latest duration from store settings (re-read to ensure we have the latest value)
      const latestDurationMinutes = currentStore?.trainingModeDurationMinutes || DEFAULT_TRAINING_DURATION_MINUTES;
      const latestDurationSeconds = latestDurationMinutes * 60;
      console.log('[Training Mode] Using duration from store settings:', { 
        latestDurationMinutes, 
        latestDurationSeconds,
        currentStoreTrainingModeDuration: currentStore?.trainingModeDurationMinutes,
        defaultDuration: DEFAULT_TRAINING_DURATION_MINUTES
      });
      
      console.log('[Training Mode] Validation passed - enabling Training Mode');
      
      // Reset training counters to 0 for fresh start
      try {
        const { generateDateCode } = await import('../utils/posOrderId');
        const channelNumberDB = (await import('../utils/indexedDB/channelNumberDB')).default;
        const posOrderIdDB = (await import('../utils/indexedDB/posOrderIdDB')).default;
        const orderNumberGenerator = (await import('../utils/orderNumberGenerator')).default;
        
        await channelNumberDB.init();
        await posOrderIdDB.init();
        
        const stationStartTime = localStorage.getItem(`stationStartTime_${storeId}`) || '04:00';
        const dateCode = await generateDateCode(new Date(), stationStartTime, storeId);
        
        // Reset channel counters (CS, TW, DL) to 0
        const channels = ['CS', 'TW', 'DL'];
        for (const prefix of channels) {
          const trainingKey = `count_${storeId}_${dateCode}_${prefix}_TRAINING_${userId}`;
          await channelNumberDB.setCounter(storeId, prefix, dateCode, 0, true, userId);
          console.log(`[Training Mode] Reset ${prefix} counter to 0 for training mode`);
        }
        
        // Reset POS order ID counter to 0
        // POS order ID uses key format: posOrderId_${storeId}_${dateCode}_TRAINING_${userId} for training mode
        const posOrderKey = `posOrderId_${storeId}_${dateCode}_TRAINING_${userId}`;
        await posOrderIdDB.setCounter(posOrderKey, 0);
        console.log(`[Training Mode] Reset POS order ID counter to 0 for training mode`);
        
        // Note: Table session counters are reset per-table when first used, so no need to reset all here
      } catch (error) {
        console.error('[Training Mode] Error resetting training counters:', error);
        // Continue even if counter reset fails
      }
      
      // Save current location before enabling training mode
      const currentLocation = window.location.pathname + window.location.search;
      console.log('[Training Mode] Saving current location:', currentLocation);
      
      // Enable training mode - store start timestamp, storeId, previous location, and duration
      // Use the latest duration value we just read (not the memoized one which might be stale)
      const state = {
        isTrainingMode: true,
        startTime: Date.now(),
        userId: userId,
        storeId: storeId, // Store storeId so we don't need to look it up later
        previousLocation: currentLocation, // Save location to restore on exit
        durationSeconds: latestDurationSeconds // Store duration at time of enabling (use latest value)
      };
      console.log('[Training Mode] Setting localStorage', { storageKey, state });
      localStorage.setItem(storageKey, JSON.stringify(state));
      setTrainingStates(prev => {
        const next = {
          ...prev,
          [userId]: state
        };
        console.log('[Training Mode] Updated trainingStates', { prev, next });
        return next;
      });
      console.log(`[Training Mode] Enabled for user: ${userId}`);
      
      // Refresh the app to start from scratch - navigate to POS dashboard
      // Use a small delay to ensure localStorage is persisted
      setTimeout(() => {
        console.log('[Training Mode] Refreshing app to start from clean state...');
        
        // Navigate to POS dashboard (clean state)
        // Use window.location to force a full page refresh
        window.location.href = '/admin/pos';
      }, 100);
    } else {
      // Disable training mode - clear training sessions and state
      console.log('[Training Mode] Disabling - clearing training data');
      
      // Get storeId if not provided
      if (!storeId) {
        storeId = localStorage.getItem('selectedStoreId');
      }
      
      // Clear all training sessions (optional cleanup for clean slate)
      if (storeId) {
        try {
          const cartDB = (await import('../utils/indexedDB/cartDB')).default;
          await cartDB.init();
          
          // Get all sessions and filter training ones
          const allSessions = await cartDB.getAllSessions(storeId);
          const trainingSessions = allSessions.filter(s => s.trainingMode === true);
          
          // Delete training sessions
          for (const session of trainingSessions) {
            try {
              await cartDB.deleteSession(session.sessionId);
              console.log(`[Training Mode] Deleted training session: ${session.sessionId}`);
            } catch (error) {
              console.error(`[Training Mode] Error deleting training session ${session.sessionId}:`, error);
            }
          }
          
          if (trainingSessions.length > 0) {
            console.log(`[Training Mode] Cleared ${trainingSessions.length} training session(s)`);
          }
        } catch (error) {
          console.error('[Training Mode] Error clearing training sessions:', error);
          // Don't throw - continue with disabling even if cleanup fails
        }
      }
      
      // Clear training mode flag
      console.log('[Training Mode] Removing from localStorage', { storageKey });
      localStorage.removeItem(storageKey);
      setTrainingStates(prev => {
        const next = { ...prev };
        delete next[userId];
        console.log('[Training Mode] Updated trainingStates (disabled)', { prev, next });
        return next;
      });
      console.log(`[Training Mode] Disabled for user: ${userId}`);
    }
  }, [canEnableTrainingMode, currentStore]);

  // Get current user's training mode (convenience method) - memoized to prevent infinite loops
  // Read directly from localStorage to avoid state update loops
  const isTrainingMode = useMemo(() => {
    if (!currentPOSUser?.id) {
      console.log('[Training Mode] isTrainingMode check - no currentPOSUser.id');
      return false;
    }
    const storageKey = `trainingMode_${currentPOSUser.id}`;
    const stored = localStorage.getItem(storageKey);
    console.log('[Training Mode] isTrainingMode check', { 
      userId: currentPOSUser.id, 
      storageKey, 
      stored: stored ? 'exists' : 'not found' 
    });
    if (!stored) return false;
    try {
      const state = JSON.parse(stored);
      if (state.startTime) {
        const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
        // Use duration from state if available, otherwise use current store setting
        const duration = state.durationSeconds || trainingDurationSeconds;
        if (elapsed >= duration) {
          console.log('[Training Mode] isTrainingMode check - expired', { elapsed, duration });
          return false; // Expired, but don't update state here
        }
      }
      const result = state.isTrainingMode || false;
      console.log('[Training Mode] isTrainingMode check - result', { result, state });
      return result;
    } catch (error) {
      console.error('[Training Mode] isTrainingMode check - error parsing state', error);
      return false;
    }
  }, [currentPOSUser?.id, trainingStates, trainingDurationSeconds]);

  // Get current user's remaining time (convenience method) - memoized to prevent infinite loops
  const remainingTime = useMemo(() => {
    if (!currentPOSUser?.id) return 0;
    return getRemainingTime(currentPOSUser.id);
  }, [currentPOSUser?.id, getRemainingTime]);

  // Auto-revert check on interval (every 10 seconds)
  useEffect(() => {
    if (!currentPOSUser?.id) return;

    const interval = setInterval(() => {
      checkAndRevertIfExpired(currentPOSUser.id);
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [currentPOSUser?.id, checkAndRevertIfExpired, trainingDurationSeconds]);

  // Auto-revert when timer expires - show modal instead of silent disable
  useEffect(() => {
    if (!currentPOSUser?.id || !isTrainingMode) {
      setShowExpirationModal(false);
      return;
    }

    const remaining = getRemainingTime(currentPOSUser.id);
    if (remaining <= 0) {
      // Timer expired - show modal
      setShowExpirationModal(true);
      return;
    }

    // Set timeout to show modal when timer reaches 0
    const timeout = setTimeout(() => {
      setShowExpirationModal(true);
      console.log(`[Training Mode] Timer expired - showing expiration modal`);
    }, remaining * 1000);

    return () => clearTimeout(timeout);
  }, [currentPOSUser?.id, isTrainingMode, getRemainingTime]);

  // Handle return to production mode from expiration modal or exit button
  const handleReturnToProduction = useCallback(async () => {
    if (!currentPOSUser?.id) return;
    
    // Get previous location before disabling
    const storageKey = `trainingMode_${currentPOSUser.id}`;
    const trainingData = localStorage.getItem(storageKey);
    let previousLocation = '/admin/pos'; // Default fallback
    
    if (trainingData) {
      try {
        const trainingState = JSON.parse(trainingData);
        if (trainingState.previousLocation) {
          previousLocation = trainingState.previousLocation;
          console.log('[Training Mode] Restoring previous location:', previousLocation);
        }
      } catch (error) {
        console.error('[Training Mode] Error parsing training state for location:', error);
      }
    }
    
    const storeId = localStorage.getItem('selectedStoreId');
    await toggleTrainingMode(currentPOSUser.id, false, storeId);
    setShowExpirationModal(false);
    
    // Navigate back to previous location instead of just reloading
    console.log('[Training Mode] Returning to previous location:', previousLocation);
    window.location.href = previousLocation;
  }, [currentPOSUser?.id, toggleTrainingMode]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    isTrainingMode,
    remainingTime,
    getTrainingMode,
    getRemainingTime,
    toggleTrainingMode,
    canEnableTrainingMode,
    checkAndRevertIfExpired,
    currentUserId: currentPOSUser?.id || null,
    showExpirationModal,
    handleReturnToProduction
  }), [isTrainingMode, remainingTime, getTrainingMode, getRemainingTime, toggleTrainingMode, canEnableTrainingMode, checkAndRevertIfExpired, currentPOSUser?.id, showExpirationModal, handleReturnToProduction]);

  return (
    <TrainingModeContext.Provider value={value}>
      {children}
    </TrainingModeContext.Provider>
  );
};

export const useTrainingMode = () => {
  const context = useContext(TrainingModeContext);
  if (context === undefined) {
    throw new Error('useTrainingMode must be used within a TrainingModeContextProvider');
  }
  return context;
};

export default TrainingModeContext;

